import { type ColumnMapping } from './column-mapping'
import { type CsvReadConfig } from './csv-import-state'
import { type LogiFieldValue } from './logi-fields'
import { type CellOverrides } from './preview'

/** サーバーが読める文字コード（画面で読んだ文字コードと同じもので読ませる）。 */
export type ImportEncoding = 'UTF8' | 'SJIS' | 'EUCJP'

/**
 * 取り込みジョブ・事前チェックに渡す「取り込み設定」。サーバーは元ファイルにこの設定を当てて、
 * 画面で見ていたのと同じ行を組み立てる（保存フォーマットではなく、実行時点の画面の内容そのもの）。
 * - kind='standard': 通常フォーマット（ヘッダー名で読む）。確認画面で修正・除外をしたときに使う
 * - kind='mapping': 汎用（画面で紐づけた列で読む）
 * - rowNo は確認画面の行番号（データ行の 1 始まり）
 */
export type ImportSetting = {
  kind: 'standard' | 'mapping'
  encoding: ImportEncoding
  readConfig: CsvReadConfig
  mapping?: Partial<Record<LogiFieldValue, number>>
  overrides?: Record<string, Partial<Record<LogiFieldValue, string>>>
  excludedRowNos?: number[]
  formatName?: string
}

/**
 * encoding-japanese の識別子をサーバーの文字コードに直す。ASCII は UTF-8 の部分集合なので UTF8 として読む。
 * それ以外（UTF-16 等）は取り込めないので null（画面で文字コードを選び直してもらう）。
 */
export const toImportEncoding = (code: string): ImportEncoding | null => {
  if (code === 'UTF8' || code === 'ASCII') return 'UTF8'
  if (code === 'SJIS') return 'SJIS'
  if (code === 'EUCJP') return 'EUCJP'
  return null
}

/** 画面のマッピング（列 → 項目）を、サーバーに渡す向き（項目 → 列）に直す。 */
export const invertColumnMapping = (
  mapping: ColumnMapping
): Partial<Record<LogiFieldValue, number>> => {
  const result: Partial<Record<LogiFieldValue, number>> = {}
  for (const [key, field] of Object.entries(mapping)) {
    if (field !== null) result[field] = Number(key)
  }
  return result
}

/** 修正値のうち、実際に値が入っているセルだけを文字列キーで渡す形に直す。 */
const toOverridesPayload = (
  overrides: CellOverrides
): ImportSetting['overrides'] => {
  const result: NonNullable<ImportSetting['overrides']> = {}
  for (const [rowNo, fields] of Object.entries(overrides)) {
    if (Object.keys(fields).length > 0) result[rowNo] = fields
  }
  return result
}

export const buildImportSetting = (params: {
  isStandard: boolean
  encoding: ImportEncoding
  readConfig: CsvReadConfig
  mapping: ColumnMapping
  overrides: CellOverrides
  excludedRowNos: number[]
  formatName: string | null
}): ImportSetting => ({
  kind: params.isStandard ? 'standard' : 'mapping',
  encoding: params.encoding,
  readConfig: params.readConfig,
  ...(params.isStandard
    ? {}
    : { mapping: invertColumnMapping(params.mapping) }),
  overrides: toOverridesPayload(params.overrides),
  excludedRowNos: [...params.excludedRowNos].sort((a, b) => a - b),
  ...(params.formatName ? { formatName: params.formatName } : {})
})
